import {
    METHOD_POST,
    METHOD_DELETE,
    METHOD_GET,
    METHOD_PUT,
    SERVER_URL
} from '../constants/api';
import { headersAuth, headersAuthSendReceiveJson } from '../headers';
import { apiHandleResponse } from '../../util/api';

/**
 * Get list of supplementary knowledge documents
 * @param options - Datatable options including page, sort, and filter
 */
export const getSuppKbDocumentsList = (options: any) => {
    // Remove null/undefined filter from payload to match API expectations
    const payload = {
        ...options,
        ...(options.filter && { filter: options.filter })
    };
    
    return fetch(`${SERVER_URL}/api/v1/genai/suppKb/documents/list`, {
        method: METHOD_POST,
        headers: headersAuthSendReceiveJson(),
        body: JSON.stringify(payload),
    }).then(apiHandleResponse);
};

/**
 * Get single supplementary knowledge document by ID
 * @param suppKbDocumentId - ID of the document to fetch
 */
export const getSuppKbDocument = (suppKbDocumentId: number) => {
    return fetch(`${SERVER_URL}/api/v1/genai/suppKb/documents/${suppKbDocumentId}`, {
        method: METHOD_GET,
        headers: headersAuthSendReceiveJson(),
    }).then(apiHandleResponse);
};

/**
 * Upload supplementary knowledge document
 * @param payload - FormData containing ContractIds[], DocumentName, Document, Description
 */
export const uploadSuppKbDocument = (payload: FormData) => {
    return fetch(`${SERVER_URL}/api/v1/genai/suppKb/documents`, {
        method: METHOD_POST,
        headers: headersAuth(),
        body: payload,
    }).then(apiHandleResponse);
};

/**
 * Update supplementary knowledge document
 * @param payload - JSON object containing id, contractIds[], documentName, description
 */
export const updateSuppKbDocument = (payload: any) => {
    return fetch(`${SERVER_URL}/api/v1/genai/suppKb/documents`, {
        method: METHOD_PUT,
        headers: headersAuthSendReceiveJson(),
        body: JSON.stringify(payload),
    }).then(apiHandleResponse);
};

/**
 * Delete supplementary knowledge document
 * @param suppKbDocumentId - ID of the document to delete
 */
export const deleteSuppKbDocument = (suppKbDocumentId: number) => {
    return fetch(`${SERVER_URL}/api/v1/genai/suppKb/documents/${suppKbDocumentId}`, {
        method: METHOD_DELETE,
        headers: headersAuthSendReceiveJson(),
    }).then(apiHandleResponse);
};

/**
 * Create supplementary document in AI API
 * @param payload - JSON object containing s3_key, filename, contract_ids, document_id, metadata
 */
export const createSupplementaryDocument = (payload: {
    s3_key: string;
    filename: string;
    contract_ids: number[];
    document_id: number;
    metadata: { type: string };
    description: string;
}) => {
    return fetch(`${SERVER_URL}/ai-api/v1/create-supplementary-document`, {
        method: METHOD_POST,
        headers: headersAuthSendReceiveJson(),
        body: JSON.stringify(payload),
    }).then(apiHandleResponse);
};

/**
 * Get supplementary document status from AI API
 */
export const getSuppKbDocumentStatus = () => {
    return fetch(`${SERVER_URL}/ai-api/v1/supplementary-documents`, {
        method: METHOD_GET,
        headers: headersAuthSendReceiveJson(),
    }).then(apiHandleResponse);
};

/**
 * Retry failed supplementary document
 * @param contractId - Contract ID
 * @param documentData - Document data including metadata
 */
export const retrySuppKbDocument = (contractId: number, documentData: any) => {
    // Parse metadata if it's a string
    let metadata = {};
    if (documentData.metadata) {
        try {
            metadata = typeof documentData.metadata === 'string' 
                ? JSON.parse(documentData.metadata) 
                : documentData.metadata;
        } catch (e) {
            console.warn('Failed to parse metadata:', e);
        }
    }
    
    const payload = {
        s3_key: documentData.s3Key,
        filename: documentData.documentName,
        contract_ids: [contractId],
        document_id: documentData.id,
        metadata: metadata,
        description: documentData.description || ''
    };
    
    return fetch(`${SERVER_URL}/ai-api/v1/create-supplementary-document?retry_flag=true`, {
        method: METHOD_POST,
        headers: headersAuthSendReceiveJson(),
        body: JSON.stringify(payload),
    }).then(apiHandleResponse);
};

/**
 * Delete supplementary document from AI backend
 * @param documentMetadataId - Document metadata ID
 */
export const deleteSuppKbDocumentFromAI = (documentMetadataId: number) => {
    return fetch(`${SERVER_URL}/ai-api/v1/refresh-after-supplementary-doc-deletion?document_metadata_id=${documentMetadataId}`, {
        method: METHOD_POST,
        headers: headersAuthSendReceiveJson(),
    }).then(apiHandleResponse);
};
